import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useResearchWorkflowState } from "../../hooks/useResearchWorkflowState";
import { researchApi, ResearchProject } from "../../services/researchApi";
import { WorkflowStage } from "../../types/researchWorkflow";
import EdgeDetailOverlay from "../Layout/EdgeDetailOverlay";
import EntityDetailOverlay from "../Layout/EntityDetailOverlay";
import Sidebar from "../Layout/Sidebar";
import AgentPanel from "./AgentPanel/AgentPanel";
import CreateProjectModal from "./CreateProjectModal";
import CreateTrackModal from "./CreateTrackModal";
import FinalSelectionView from "./FinalSelection/FinalSelectionView";
import LongListView from "./LongList/LongListView";
import ProjectListModal from "./ProjectListModal";
import ProjectOverview from "./ProjectOverview";
import RequirementsEditor from "./Requirements/RequirementsEditor";
import ResearchWorkflowHeader from "./ResearchWorkflowHeader";
import ResearchWorkflowSidebar, {
  ProjectSection,
} from "./ResearchWorkflowSidebar";
import ShareProjectDialog from "./ShareProjectDialog";
import DomainCandidateOverlay from "./shared/DomainCandidateOverlay";
import ShortListView from "./ShortList/ShortListView";

const ResearchWorkflowView: React.FC = () => {
  const {
    state,
    actions,
    loading: entitiesLoading,
    activitiesLoading,
    requirementsEditMode,
    requirementsSaving,
    currentTrackId,
  } = useResearchWorkflowState();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const { hasFeature } = useAuth();
  // research_viewer-only accounts cannot create projects (backend enforces too)
  const canCreateProject = hasFeature("research");
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createTrackModalOpen, setCreateTrackModalOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState<ResearchProject | null>(
    null,
  );
  const [projectLoading, setProjectLoading] = useState(false);
  const [localTrackId, setLocalTrackId] = useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Active section: overview or a specific track+stage
  const [activeSection, setActiveSection] = useState<ProjectSection>({
    kind: "overview",
  });
  // Ref to avoid re-loading the same track
  const loadedTrackRef = useRef<string | null>(null);

  // Entity click handler for opening detail view
  const handleEntityClick = useCallback(
    (entityId: string) => {
      const params = new URLSearchParams(searchParams);
      params.delete("detail_relationship_id");
      params.set("detail_entity_id", entityId);
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  // Handler to close entity detail overlay
  const handleCloseEntityDetail = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("detail_entity_id");
    setSearchParams(params, { replace: false });
  };

  // Handler to close relationship detail overlay
  const handleCloseRelationshipDetail = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("detail_relationship_id");
    setSearchParams(params, { replace: false });
  };

  // Handler for entity navigation within overlay
  const handleEntityNavigation = (entityId: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete("detail_relationship_id");
    params.set("detail_entity_id", entityId);
    setSearchParams(params, { replace: false });
  };

  // Handler for opening entity in graph view
  const handleOpenInGraph = (entityId: string) => {
    const graphParams = new URLSearchParams();
    graphParams.set("seed_entity", entityId);
    graphParams.set("org_structure", "parents_subsidiaries");
    navigate(`/graph?${graphParams.toString()}`);
  };

  const handleOpenInMap = (
    entityName: string,
    entityType: string,
    entityId: string,
  ) => {
    if (entityType === "organization") {
      const facilityOwnerData = JSON.stringify([
        { id: entityId, name: entityName },
      ]);
      navigate(
        `/map?facilityOwnerCompanies=${encodeURIComponent(facilityOwnerData)}&facilityOwnerMaxTiers=1`,
      );
    } else {
      navigate(`/map?search=${encodeURIComponent(entityName)}`);
    }
  };

  // Handle clicking anywhere on the page to close overlay (except on interactive elements)
  const handlePageClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;

    // Check if the click is on the sidebar or its children
    const sidebar = target.closest("[data-sidebar]");
    if (sidebar) {
      return;
    }

    // Check if clicking on overlay content itself
    const overlayContent = target.closest("[data-overlay-content]");
    if (overlayContent) {
      return;
    }

    // Check if clicking on MUI portaled components (menus, popovers, dialogs, etc.)
    const isMuiPortal = target.closest(
      ".MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiDialog-root, .MuiTooltip-popper, .MuiAutocomplete-popper",
    );
    if (isMuiPortal) {
      return;
    }

    // Only close if there's an overlay open and click is outside interactive elements
    if (
      searchParams.get("detail_entity_id") ||
      searchParams.get("detail_relationship_id")
    ) {
      const params = new URLSearchParams(searchParams);
      params.delete("detail_entity_id");
      params.delete("detail_relationship_id");
      setSearchParams(params, { replace: false });
    }
  };

  // Sync URL ↔ activeSection
  useEffect(() => {
    const urlTrack = searchParams.get("track");
    const urlStage = searchParams.get("stage") as WorkflowStage | null;

    if (urlTrack && urlStage) {
      // URL has a track+stage — sync into state if different
      if (
        activeSection.kind !== "track" ||
        activeSection.trackId !== urlTrack ||
        activeSection.stage !== urlStage
      ) {
        setActiveSection({ kind: "track", trackId: urlTrack, stage: urlStage });
      }
    } else if (!urlTrack) {
      // No track in URL → overview
      if (activeSection.kind !== "overview") {
        setActiveSection({ kind: "overview" });
      }
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load track data when the active track changes
  useEffect(() => {
    if (activeSection.kind !== "track") {
      return;
    }
    const trackId = activeSection.trackId;

    // Only reload if we're switching to a different track
    if (loadedTrackRef.current === trackId) {
      // Same track — just sync the stage
      actions.setCurrentStage(activeSection.stage);
      return;
    }

    loadedTrackRef.current = trackId;
    setLocalTrackId(trackId);
    actions.setCurrentStage(activeSection.stage);

    // Load data for the new track
    const loadTrackData = async () => {
      try {
        await actions.loadRequirements(trackId);
        await actions.loadEntities(trackId);
        await actions.loadActivities(trackId);
      } catch (error) {
        console.error("Failed to load track data:", error);
      }
    };
    loadTrackData();
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load project data when projectId changes
  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) {
        setCurrentProject(null);
        setLocalTrackId(null);
        loadedTrackRef.current = null;
        return;
      }

      try {
        setProjectLoading(true);

        // Clear previous track ID immediately to prevent stale data
        setLocalTrackId(null);
        loadedTrackRef.current = null;

        // Fetch project details (includes tracks array)
        const project = await researchApi.getProject(projectId);
        setCurrentProject(project);

        // Restore section from URL or default to overview
        const urlTrack = searchParams.get("track");
        const urlStage = searchParams.get("stage") as WorkflowStage | null;

        if (urlTrack && urlStage) {
          setActiveSection({
            kind: "track",
            trackId: urlTrack,
            stage: urlStage,
          });
        } else {
          // Default to project overview so the user can set up the project
          setActiveSection({ kind: "overview" });
        }
      } catch (error) {
        console.error("Failed to load project data:", error);
      } finally {
        setProjectLoading(false);
      }
    };

    loadProject();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle section change from sidebar
  const handleSectionChange = useCallback(
    (section: ProjectSection) => {
      setActiveSection(section);
      const params = new URLSearchParams(searchParams);
      if (section.kind === "overview") {
        params.delete("track");
        params.delete("stage");
      } else {
        params.set("track", section.trackId);
        params.set("stage", section.stage);
      }
      // Close any open detail overlays when navigating via sidebar
      params.delete("detail_entity_id");
      params.delete("detail_relationship_id");
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  // Project management handlers
  const handleLoadProject = useCallback(
    (project: ResearchProject) => {
      // Navigate to the project URL
      navigate(`/research/${project.id}`);
    },
    [navigate],
  );

  const handleProjectCreated = useCallback(
    (project: ResearchProject) => {
      // Navigate to the new project URL
      navigate(`/research/${project.id}`);
    },
    [navigate],
  );

  const handleOpenLoadModal = useCallback(() => {
    setLoadModalOpen(true);
  }, []);

  const handleOpenCreateModal = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleRenameTrack = useCallback(
    async (newName: string) => {
      if (activeSection.kind !== "track") return;
      try {
        await researchApi.updateTrack(activeSection.trackId, {
          name: newName,
        });
        // Refresh project to update track names everywhere
        const updated = await researchApi.getProject(projectId!);
        setCurrentProject(updated);
      } catch (err) {
        console.error("Failed to rename track:", err);
      }
    },
    [activeSection, projectId],
  );

  const handleDeleteTrack = useCallback(
    async (trackId: string) => {
      try {
        await researchApi.deleteTrack(trackId);
        // Refresh project to update track list
        const updated = await researchApi.getProject(projectId!);
        setCurrentProject(updated);
        // If the deleted track was active, go back to overview
        if (
          activeSection.kind === "track" &&
          activeSection.trackId === trackId
        ) {
          handleSectionChange({ kind: "overview" });
        }
      } catch (err) {
        console.error("Failed to delete track:", err);
      }
    },
    [activeSection, projectId, handleSectionChange],
  );

  // Handle generated requirements from the AI flow
  const handleRequirementsGenerated = useCallback(
    (runSummary: any) => {
      const generated = runSummary?.generated_requirements;
      if (!generated?.criteria) return;

      // Map generated criteria to frontend Criterion format
      const criteria = generated.criteria.map((c: any) => ({
        id: c.criterion_id || `crit-${Date.now()}-${Math.random()}`,
        label: c.label || "",
        value: c.description || "",
        weight: Math.min(5, Math.max(1, c.weight || 3)) as 1 | 2 | 3 | 4 | 5,
        mode: "fuzzy" as const,
        rubric:
          c.rubric && Object.keys(c.rubric).length > 0
            ? { anchors: c.rubric, scale_min: 1, scale_max: 5 }
            : undefined,
      }));

      // Update requirements state with generated criteria
      actions.updateRequirements({
        objective: generated.objective || state.requirements.objective || "",
        criteria,
      });

      // Switch to edit mode so user can review and modify
      actions.setRequirementsEditMode(true);
    },
    [actions, state.requirements.objective],
  );

  // Show project selection prompt if no project is selected
  if (!projectId) {
    return (
      <Box sx={{ display: "flex", height: "100%" }}>
        <Sidebar />
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            p: 4,
          }}
        >
          <Typography variant="h4" color="text.secondary">
            No Project Selected
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please select or create a research project to get started.
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            {canCreateProject && (
              <Button variant="contained" onClick={handleOpenCreateModal}>
                Create New Project
              </Button>
            )}
            <Button variant="outlined" onClick={handleOpenLoadModal}>
              Load Existing Project
            </Button>
          </Box>
        </Box>

        {/* Modals rendered outside the layout */}
        <ProjectListModal
          open={loadModalOpen}
          onClose={() => setLoadModalOpen(false)}
          onLoadProject={handleLoadProject}
          onCreateNew={
            canCreateProject
              ? () => {
                  setLoadModalOpen(false);
                  setCreateModalOpen(true);
                }
              : undefined
          }
        />
        <CreateProjectModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onProjectCreated={handleProjectCreated}
        />
      </Box>
    );
  }

  // Derive active track name for the breadcrumb header
  const activeTrackName =
    activeSection.kind === "track"
      ? currentProject?.tracks?.find((t) => t.id === activeSection.trackId)
          ?.name
      : undefined;

  // Permission-derived read-only flags.
  //
  // REQ_EDITOR behaves like a VIEWER everywhere *except* the
  // Requirements editor, where it may edit criteria and run assistant-based
  // generation. So all non-requirements views treat it as a viewer, while the
  // Requirements editor is read-only only for true VIEWERs.
  const permissionLevel = currentProject?.permission_level;
  const isRequirementsEditor = permissionLevel === "REQ_EDITOR";
  // research_viewer-only accounts are globally read-only regardless of the
  // project role they hold (the backend enforces the same cap with 403s).
  const isGlobalViewer = !hasFeature("research");
  const isViewer =
    permissionLevel === "VIEWER" || isRequirementsEditor || isGlobalViewer;
  const isRequirementsViewer = permissionLevel === "VIEWER" || isGlobalViewer;

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        backgroundColor: "background.default",
      }}
      onClick={handlePageClick}
    >
      {/* Main App Sidebar */}
      <Sidebar />

      {/* Right of app sidebar: project header + (sidebar + content) */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Breadcrumb header: [Load | New] Project Name › Track Name */}
        <ResearchWorkflowHeader
          projectName={currentProject?.name || "Research Project"}
          trackName={
            activeTrackName ||
            (activeSection.kind === "overview" ? "Overview" : undefined)
          }
          project={currentProject}
          onShareClick={() => setShareDialogOpen(true)}
          onLoadProject={handleOpenLoadModal}
          onNewProject={handleOpenCreateModal}
          onRenameTrack={handleRenameTrack}
          isTrack={activeSection.kind === "track"}
        />

        {/* Below header: sidebar + content row */}
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Research Workflow Sidebar */}
          <ResearchWorkflowSidebar
            tracks={currentProject?.tracks || []}
            activeSection={activeSection}
            stages={state.stages}
            onSectionChange={handleSectionChange}
            onAddTrack={() => setCreateTrackModalOpen(true)}
            onDeleteTrack={handleDeleteTrack}
            isViewer={isViewer}
          />

          {/* Center: Section-specific view */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 3,
              backgroundColor: "#1e1e1e",
            }}
          >
            {activeSection.kind === "overview" && currentProject && (
              <ProjectOverview
                project={currentProject}
                onProjectUpdated={setCurrentProject}
              />
            )}
            {activeSection.kind === "track" && (
              <>
                {state.currentStage === "requirements" && (
                  <RequirementsEditor
                    requirements={state.requirements}
                    onUpdateObjective={actions.updateObjective}
                    onAddCriterion={actions.addCriterion}
                    onUpdateCriterion={actions.updateCriterion}
                    onRemoveCriterion={actions.removeCriterion}
                    isEditMode={requirementsEditMode}
                    onToggleEditMode={() =>
                      actions.setRequirementsEditMode(!requirementsEditMode)
                    }
                    onSave={() =>
                      localTrackId && actions.saveRequirements(localTrackId)
                    }
                    onCancel={() =>
                      localTrackId &&
                      actions.cancelRequirementsEdit(localTrackId)
                    }
                    isSaving={requirementsSaving}
                    trackId={localTrackId || undefined}
                    onRequirementsGenerated={handleRequirementsGenerated}
                    isViewer={isRequirementsViewer}
                  />
                )}
                {state.currentStage === "longList" && (
                  <LongListView
                    entities={state.entities}
                    trackId={currentTrackId || ""}
                    onApprove={(entityId) =>
                      actions.updateEntityStatus(entityId, "approved")
                    }
                    onReject={(entityId, reason) =>
                      actions.updateEntityStatus(entityId, "rejected", reason)
                    }
                    onBulkApprove={(entityIds) =>
                      actions.bulkUpdateEntityStatus(entityIds, "approved")
                    }
                    onBulkReject={(entityIds) =>
                      actions.bulkUpdateEntityStatus(entityIds, "rejected")
                    }
                    onBulkFinal={(entityIds) =>
                      actions.bulkUpdateEntityStatus(entityIds, "final")
                    }
                    onRunDiscovery={actions.generateLongList}
                    onStopDiscovery={actions.cancelDiscovery}
                    onEntityClick={handleEntityClick}
                    onCandidatesAdded={() => {
                      if (currentTrackId) actions.loadEntities(currentTrackId);
                    }}
                    isRunning={state.activeWork.discovery.length > 0}
                    isLoading={entitiesLoading}
                    isViewer={isViewer}
                  />
                )}
                {state.currentStage === "shortList" && (
                  <ShortListView
                    entities={state.entities}
                    trackId={currentTrackId || ""}
                    requirements={state.requirements}
                    onAddToShortList={(entityIds) =>
                      actions.bulkUpdateEntityStatus(entityIds, "shortlisted")
                    }
                    onRemoveFromShortList={(entityId) =>
                      actions.updateEntityStatus(entityId, "candidate")
                    }
                    onMoveToFinal={(entityId) =>
                      actions.updateEntityStatus(entityId, "final")
                    }
                    onBulkMoveToFinal={(entityIds) =>
                      actions.bulkUpdateEntityStatus(entityIds, "final")
                    }
                    onBulkMoveToDiscovery={(entityIds) =>
                      actions.bulkUpdateEntityStatus(entityIds, "candidate")
                    }
                    onSelectEntity={actions.selectEntity}
                    onEntityClick={handleEntityClick}
                    onTriggerEvaluation={actions.triggerEvaluation}
                    onStopEvaluation={actions.cancelAllEvaluations}
                    onCancelEvaluation={actions.cancelEvaluation}
                    onCandidatesAdded={() => {
                      if (currentTrackId) actions.loadEntities(currentTrackId);
                    }}
                    onSaveWeights={async (updatedWeights) => {
                      if (!currentTrackId) return;
                      const updatedRequirements = {
                        ...state.requirements,
                        criteria: state.requirements.criteria.map((c) => {
                          const updated = updatedWeights.find((u) => u.id === c.id);
                          return updated !== undefined
                            ? { ...c, weight: updated.weight }
                            : c;
                        }),
                      };
                      await researchApi.updateTrackRequirements(
                        currentTrackId,
                        updatedRequirements,
                      );
                      await actions.loadRequirements(currentTrackId);
                    }}
                    isRunning={
                      state.activeWork.evaluation.queued +
                        state.activeWork.evaluation.running >
                      0
                    }
                    isLoading={entitiesLoading}
                    isViewer={isViewer}
                  />
                )}
                {state.currentStage === "finalSelection" && (
                  <FinalSelectionView
                    entities={state.entities}
                    trackId={currentTrackId || ""}
                    requirements={state.requirements}
                    onRemoveFromFinal={(entityId) =>
                      actions.updateEntityStatus(entityId, "shortlisted")
                    }
                    onBulkDismiss={(entityIds) =>
                      actions.bulkUpdateEntityStatus(entityIds, "shortlisted")
                    }
                    onSelectEntity={actions.selectEntity}
                    onEntityClick={handleEntityClick}
                    isLoading={entitiesLoading}
                    isViewer={isViewer}
                  />
                )}
              </>
            )}
          </Box>

          {/* Right: Agent Panel — only shown when a track is active */}
          {activeSection.kind === "track" && (
            <AgentPanel
              currentStage={state.currentStage}
              activeWork={state.activeWork}
              activities={state.agentActivities}
              activitiesLoading={activitiesLoading}
              selectedEntity={
                state.selectedEntityId
                  ? state.entities.find((e) => e.id === state.selectedEntityId)
                  : undefined
              }
              onClearSelection={() => actions.selectEntity(undefined)}
            />
          )}
        </Box>
      </Box>

      {/* Project Management Modals */}
      <ProjectListModal
        open={loadModalOpen}
        onClose={() => setLoadModalOpen(false)}
        onLoadProject={handleLoadProject}
        onCreateNew={
          canCreateProject
            ? () => {
                setLoadModalOpen(false);
                setCreateModalOpen(true);
              }
            : undefined
        }
      />
      <CreateProjectModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onProjectCreated={handleProjectCreated}
      />
      {currentProject && (
        <CreateTrackModal
          open={createTrackModalOpen}
          onClose={() => setCreateTrackModalOpen(false)}
          onTrackCreated={async () => {
            const updated = await researchApi.getProject(currentProject.id);
            setCurrentProject(updated);
          }}
          projectId={currentProject.id}
        />
      )}
      {currentProject && (
        <ShareProjectDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          project={currentProject}
          onShareUpdated={async () => {
            const updated = await researchApi.getProject(currentProject.id);
            setCurrentProject(updated);
          }}
        />
      )}

      {/* Entity Detail Overlay — domain candidates (not in the graph yet)
          render from their webcontent profile, everything else from the graph */}
      {searchParams.get("detail_entity_id") &&
        (searchParams.get("detail_entity_id")!.startsWith("domain:") ? (
          <DomainCandidateOverlay
            kgEntityId={searchParams.get("detail_entity_id")!}
            trackId={currentTrackId || ""}
            onClose={handleCloseEntityDetail}
            onEntityClick={handleEntityNavigation}
            onRemoved={() => {
              if (currentTrackId) actions.loadEntities(currentTrackId);
            }}
          />
        ) : (
          <EntityDetailOverlay
            entityId={searchParams.get("detail_entity_id")!}
            onClose={handleCloseEntityDetail}
            onEntityClick={handleEntityNavigation}
            onOpenInGraph={handleOpenInGraph}
            onOpenInMap={handleOpenInMap}
          />
        ))}

      {/* Relationship Detail Overlay */}
      {searchParams.get("detail_relationship_id") && (
        <EdgeDetailOverlay
          relationshipId={searchParams.get("detail_relationship_id")!}
          onClose={handleCloseRelationshipDetail}
          onEntityClick={handleEntityNavigation}
        />
      )}
    </Box>
  );
};

export default ResearchWorkflowView;
